import { Outlet, createRootRouteWithContext, useNavigate, useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { CartFab } from '../components/CartFab';
import { SuspendedPage } from '../components/SuspendedPage';
import { getSubdomain, isMarketingSite, isTenantSite } from '../lib/hostname';
import { getRootUrl } from '../lib/urls';

interface RouterContext {
  queryClient: QueryClient;
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
});

function RootLayout() {
  const navigate = useNavigate();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const marketing = isMarketingSite();
  const tenant = isTenantSite();

  const { data: tenantInfo } = useQuery({
    queryKey: ['tenant-info'],
    queryFn: async () => {
      const res = await fetch('/api/tenant', { credentials: 'include' });
      if (!res.ok) return null;
      return (await res.json()) as { id: string; slug: string; businessName: string; status: string } | null;
    },
    enabled: tenant,
    staleTime: 60_000,
  });

  useEffect(() => {
    if (tenant) {
      // On tenant subdomain, block marketing-only routes
      if (pathname === '/signup') {
        void navigate({ to: '/', replace: true });
      }
    }
  }, [tenant, pathname, navigate]);

  // Set page title based on context
  useEffect(() => {
    if (marketing) {
      document.title = 'سنديان — الأداة الأولى لتسعير الأثاث وتوليد العملاء المحتملين';
    }
  }, [marketing]);

  if (tenant && tenantInfo?.status === 'suspended' && !pathname.startsWith('/admin')) {
    return <SuspendedPage />;
  }

  const showCartFab = tenant && !pathname.startsWith('/admin') && !pathname.startsWith('/signup');
  const isDemo = tenant && getSubdomain() === 'demo' && !pathname.startsWith('/admin');

  return (
    <div className="min-h-full flex flex-col">
      {isDemo ? (
        <div className="bg-oud-900 text-sand px-5 py-2 text-center text-sm font-bold flex items-center justify-center gap-3 z-30">
          <span className="text-cream/70">هذا عرض تجريبي</span>
          <a href="/admin" className="bg-gold/20 border border-gold-soft/30 text-gold-soft rounded-full px-3 py-0.5 text-xs font-bold hover:bg-gold/30 transition-colors">
            لوحة التحكم
          </a>
          <a href={`${getRootUrl()}/signup`} className="text-cream/50 hover:text-cream text-xs underline underline-offset-2">
            أنشئ متجرك
          </a>
        </div>
      ) : null}
      <Outlet />
      {showCartFab ? <CartFab /> : null}
    </div>
  );
}
